import { Modal, Spinner, Typology } from '@/components';
import { DangerIcon } from '@/components/Icons';
import { mapJiraReference } from '@/modules/Requests/mappers';
import { useGetRequestDetails } from '@/modules/Requests/queries';
import { TypologyValue } from '@/shared';
import { goToJiraRequest } from '@/shared/utils/jira-links';
import { isActionRequired } from '@/utils';
import { t } from 'i18next';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import {
  ActionConfig,
  RequestActions,
  RequestActivity,
  RequestBaseInfo,
} from './components';

interface RequestDetailsModalProps {
  requestId: number;
  isOpen: boolean;
  onClose: () => void;
}

const Banner = () => {
  return (
    <div className="flex justify-center bg-orange-100 py-2.5 text-sm text-orange">
      <div className="flex gap-1.5">
        <div className="flex flex-row items-center gap-1 font-black">
          <DangerIcon className="size-4 stroke-[1.5]" />
          {t('required_action_banner_title_1')}
        </div>
        <div className="font-normal">{t('required_action_banner_title_2')}</div>
      </div>
    </div>
  );
};

export const RequestDetailsModal = ({
  requestId,
  isOpen,
  onClose,
}: RequestDetailsModalProps) => {
  const { data: request, isLoading } = useGetRequestDetails(requestId);

  const titleContent = useMemo(() => {
    if (isLoading || !request) {
      return null;
    }

    return (
      <div className="flex gap-2.5">
        <Typology type={request.typology} />
        <div className="flex items-center gap-1 text-sm font-normal text-gray">
          <Trans
            className="text-gray"
            i18nKey="request_details_modal_title"
            values={{
              typologyDescription: t(
                `typology_description_${TypologyValue[request.typology]}`,
              ),
              reference: mapJiraReference(request.reference),
            }}
          />
        </div>
      </div>
    );
  }, [isLoading, request]);

  const actions = useMemo<ActionConfig[]>(
    () => [
      {
        variant: 'outline',
        className: 'border-blue-500 font-black text-blue-500',
        onClick: onClose,
        buttonText: t('back'),
      },
      {
        variant: 'primary',
        className: 'font-black',
        onClick: () => request?.reference && goToJiraRequest(request.reference),
        buttonText: t('go_to_jira'),
      },
    ],
    [onClose, request?.reference],
  );

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      allowClickOutsideToClose
      title={titleContent}
      banner={isActionRequired(request?.status) && !isLoading && <Banner />}
      className="flex flex-col gap-[30px]"
    >
      {isLoading || !request ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-10 text-gray">
          <RequestBaseInfo request={request} />
          <RequestActivity requestId={request.id} />
          <RequestActions actions={actions} />
        </div>
      )}
    </Modal>
  );
};
